import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { debounceTime, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

import { NzTableModule, NzTableQueryParams } from 'ng-zorro-antd/table';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzMessageService } from 'ng-zorro-antd/message';

import { InputModule, SelectComponent } from '@beautyapp/form';
import { ServicePackageService, ServicePackage } from '@beautyapp/core';
import { NgxPermissionsModule } from 'ngx-permissions';
import { SellPackageModalComponent } from '../sell-package-modal/sell-package-modal.component';
import { openPackageFormModal } from '../package-form/open-package-form-modal';

@Component({
  selector: 'beautyapp-package-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    NzTableModule,
    NzButtonModule,
    NzIconModule,
    NzTagModule,
    NzCardModule,
    NzSpaceModule,
    NzToolTipModule,
    NzPopconfirmModule,
    InputModule,
    SelectComponent,
    NgxPermissionsModule,
  ],
  templateUrl: './package-list.component.html',
})
export class PackageListComponent {
  private packageService = inject(ServicePackageService);
  private router = inject(Router);
  private modal = inject(NzModalService);
  private message = inject(NzMessageService);
  private translate = inject(TranslateService);

  filterForm = new FormGroup({
    q: new FormControl<string | null>(null),
    isActive: new FormControl<string | null>(null),
  });

  page = signal(1);
  pageSize = signal(10);

  activeOptions = computed(() => [
    { label: this.translate.instant('servicePackages.active'), value: 'true' },
    {
      label: this.translate.instant('servicePackages.inactive'),
      value: 'false',
    },
  ]);

  filterValues = toSignal(
    this.filterForm.valueChanges.pipe(debounceTime(300)),
    { initialValue: this.filterForm.value }
  );

  packagesResource = rxResource({
    params: () => {
      const filters = this.filterValues();
      return {
        page: this.page(),
        pageSize: this.pageSize(),
        isActive: filters?.isActive || undefined,
        q: filters?.q?.trim() || undefined,
      };
    },
    stream: ({ params }) =>
      this.packageService.getAll(params).pipe(
        catchError(() =>
          of({
            data: [],
            meta: { page: 1, pageSize: 10, pageCount: 0, itemCount: 0 },
          })
        )
      ),
  });

  loading = computed(() => this.packagesResource.isLoading());
  packages = computed(() => this.packagesResource.value()?.data || []);
  total = computed(() => this.packagesResource.value()?.meta?.itemCount || 0);

  onQueryParamsChange(params: NzTableQueryParams): void {
    this.page.set(params.pageIndex);
    this.pageSize.set(params.pageSize);
  }

  clearFilters(): void {
    this.filterForm.reset();
    this.page.set(1);
  }

  createPackage(): void {
    openPackageFormModal(this.modal, {
      title: this.translate.instant('servicePackages.form.createTitle'),
    }).afterClose.subscribe((saved) => {
      // Jump back to the first page so the newly added package is visible.
      // A no-op page.set(1) (already on page 1) won't refetch, so reload then
      // (beaut-299).
      if (saved) {
        if (this.page() === 1) {
          this.packagesResource.reload();
        } else {
          this.page.set(1);
        }
      }
    });
  }

  viewPurchases(): void {
    this.router.navigate(['/service-packages/purchases']);
  }

  viewPackage(pkg: ServicePackage): void {
    this.router.navigate(['/service-packages', pkg.id]);
  }

  editPackage(pkg: ServicePackage): void {
    openPackageFormModal(this.modal, {
      packageId: pkg.id,
      title: this.translate.instant('servicePackages.form.editTitle'),
    }).afterClose.subscribe((saved) => {
      if (saved) this.packagesResource.reload();
    });
  }

  deletePackage(pkg: ServicePackage): void {
    this.packageService.delete(pkg.id).subscribe({
      next: () => {
        this.message.success(
          this.translate.instant('servicePackages.messages.deleted')
        );
        // If we just removed the only row on a non-first page, step back one
        // page so the list doesn't land on a now-empty page with the pager
        // gone; the page() signal change refetches on its own (beaut-299).
        if (this.packages().length === 1 && this.page() > 1) {
          this.page.set(this.page() - 1);
        } else {
          this.packagesResource.reload();
        }
      },
      error: () => {
        this.message.error(
          this.translate.instant('servicePackages.messages.deleteError')
        );
      },
    });
  }

  sellPackage(pkg: ServicePackage): void {
    const modalRef = this.modal.create({
      nzTitle: this.translate.instant('servicePackages.sellModal.title'),
      nzContent: SellPackageModalComponent,
      nzWidth: 600,
      nzFooter: null,
      nzData: {
        packageId: pkg.id,
        packageName: pkg.name,
        packagePrice: pkg.price,
      },
    });

    modalRef.afterClose.subscribe((result) => {
      if (result?.success) {
        this.message.success(
          this.translate.instant('servicePackages.messages.sold')
        );
        this.packagesResource.reload();
      }
    });
  }

  getItemsCount(pkg: ServicePackage): number {
    return pkg.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
  }
}
